import { Box } from '@mui/material';
import { Chain, ChainType } from '@ankr.com/chains-list';
import { t } from '@ankr.com/common';
import { useMemo } from 'react';

import { ChainGroupID, EndpointGroup } from 'modules/endpoints/types';
import { useSSOAuth } from 'modules/auth/hooks/useSSOAuth';

import { EndpointPlaceholder } from '../EndpointPlaceholder';
import { EndpointsHeader } from '../EndpointsHeader';
import { Feature, MainEndpoints } from '../MainEndpoints';
import { GRPCEndpoints } from '../GRPCEndpoints';
import { VrpcNote } from '../VrpcNote';
import { WsFeatureEndpoints } from '../WsFeatureEndpoints';
import { root } from '../../const';
import { useEndpointsStyles } from './EndpointsStyles';
import { useWsFeatureEndpoints } from '../WsFeatureEndpoints/useWsFeatureEndpoints';

export interface EndpointsProps {
  publicChain: Chain;
  chainType: ChainType;
  group: EndpointGroup;
  // Multichain placeholder
  placeholder?: string;
  userEndpointToken?: string;
}

const checkComingSoonLabel = (publicChain: Chain, chainType: ChainType) => {
  switch (chainType) {
    case ChainType.Mainnet:
      return publicChain.isComingSoon;

    case ChainType.Testnet:
      return publicChain?.testnets?.[0]?.isComingSoon;

    case ChainType.Devnet:
      return publicChain?.devnets?.[0]?.isComingSoon;

    case ChainType.Signet:
      return publicChain?.signets?.[0]?.isComingSoon;

    default:
      return false;
  }
};

export const Endpoints = ({
  chainType,
  group,
  placeholder,
  publicChain,
  userEndpointToken,
}: EndpointsProps) => {
  const { handleSignIn } = useSSOAuth();
  const { classes } = useEndpointsStyles();

  const hasComingSoonLabel = useMemo(
    () => checkComingSoonLabel(publicChain, chainType),
    [publicChain, chainType],
  );

  const { wss } = useWsFeatureEndpoints(group);

  const renderContent = useMemo(() => {
    if (hasComingSoonLabel) {
      return (
        <EndpointPlaceholder
          label={t('chains.coming-soon')}
          title={
            <EndpointsHeader
              title={t(`${root}.endpoints.title`, {
                chainName: group.chainName,
                urls: 1,
              })}
            />
          }
        />
      );
    }

    // Multichain placeholder
    if (placeholder) {
      return (
        <EndpointPlaceholder
          onClick={handleSignIn}
          lockedLabel={placeholder}
          title={
            <EndpointsHeader
              title={t('chain-item.get-started.endpoints.title-multichain')}
            />
          }
        />
      );
    }

    return (
      <>
        <MainEndpoints
          chainType={chainType}
          feature={Feature.RPC}
          group={group}
          publicChain={publicChain}
        />
        <MainEndpoints
          chainType={chainType}
          feature={Feature.REST}
          group={group}
          publicChain={publicChain}
        />
        <WsFeatureEndpoints
          title={t(`${root}.endpoints.websocket-title`)}
          group={group}
          wss={wss}
        />
        {userEndpointToken && (
          <GRPCEndpoints group={group} userEndpointToken={userEndpointToken} />
        )}
        {group.id === ChainGroupID.VRPC && <VrpcNote />}
      </>
    );
  }, [
    chainType,
    group,
    handleSignIn,
    hasComingSoonLabel,
    placeholder,
    publicChain,
    userEndpointToken,
    wss,
  ]);

  return <Box className={classes.endpointsList}>{renderContent}</Box>;
};
